import { supabase } from '@/integrations/supabase/client';
import { fetchMyProfilePrivate, type UserProfile } from '@/services/profile';
import type { CurrentSellerStatus } from '@/hooks/useCurrentSellerStatus';
import { CURRENT_PROFILE_KEY } from '@/hooks/useCurrentProfile';
import type { QueryClient } from '@tanstack/react-query';

export interface SellerOnboardingResult {
  needsOnboarding: boolean;
  acceptedAt: string | null;
  isVerified: boolean;
  planLabel: string | null;
  creditsAmount: number;
  boostAmount: number;
}

/**
 * Check seller onboarding status from cached profile + seller status.
 * No extra network query needed — reads from React Query cache.
 */
export function checkSellerOnboardingFromCache(
  profile: UserProfile | null | undefined,
  sellerStatus: CurrentSellerStatus | null | undefined,
): SellerOnboardingResult {
  if (!profile) {
    return { needsOnboarding: false, acceptedAt: null, isVerified: false, planLabel: null, creditsAmount: 2, boostAmount: 0 };
  }

  return {
    needsOnboarding: profile.is_verified === true && profile.seller_onboarding_accepted_at === null,
    acceptedAt: profile.seller_onboarding_accepted_at,
    isVerified: profile.is_verified === true,
    planLabel: sellerStatus?.sellerTypeLabel || null,
    creditsAmount: sellerStatus?.negotiationsPerYear || 2,
    boostAmount: profile.boost_plan_total || 0,
  };
}

/**
 * Legacy: Check seller onboarding via direct query (for non-cached contexts).
 * Prefer checkSellerOnboardingFromCache when profile/seller data is available.
 */
export async function checkSellerOnboardingStatus(userId: string): Promise<SellerOnboardingResult> {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      is_verified,
      boost_plan_total,
      seller_types!seller_type_id (
        label,
        negotiations_per_year
      )
    `)
    .eq('user_id', userId)
    .single();

  if (error) {
    console.error('Error checking seller onboarding status:', error);
    return { 
      needsOnboarding: false, 
      acceptedAt: null, 
      isVerified: false,
      planLabel: null,
      creditsAmount: 2,
      boostAmount: 0,
    };
  }

  const sellerType = data.seller_types as { label: string; negotiations_per_year: number } | null;
  // Campo privado: legível apenas pelo próprio usuário via RPC segura.
  const { seller_onboarding_accepted_at } = await fetchMyProfilePrivate();

  return {
    needsOnboarding: data.is_verified === true && seller_onboarding_accepted_at === null,
    acceptedAt: seller_onboarding_accepted_at,
    isVerified: data.is_verified === true,
    planLabel: sellerType?.label || null,
    creditsAmount: sellerType?.negotiations_per_year || 2,
    boostAmount: data.boost_plan_total || 0,
  };
}

/**
 * Mark the seller onboarding as accepted
 */
export async function acceptSellerOnboarding(
  userId: string,
  queryClient?: QueryClient,
): Promise<boolean> {
  const { error } = await supabase
    .from('profiles')
    .update({ 
      seller_onboarding_accepted_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId);

  if (error) {
    console.error('Error accepting seller onboarding:', error);
    return false;
  }

  // Invalida o cache do perfil para evitar que o modal reapareça com dados stale
  queryClient?.invalidateQueries({ queryKey: [CURRENT_PROFILE_KEY] });
  return true;
}
